import {
  CoreApi,
  CornerSolutionComponent,
  Product,
  ProductPartial,
  ProductType,
  SlidingDoorComponent
} from '@inter-ikea-storageone/range';
import {
  CornerAddonFrameComponent,
  CornerRegularFrameComponent,
  CustomSlidingDoorComponent
} from '@inter-ikea-storageone/range-pax';
import { ComponentIds, Entity, HierarchyUtils } from '@inter-ikea-core/core';
import {
  matchDepth,
  matchDesign,
  matchOrientation,
  matchWidth
} from 'rangeConfig/shared/filters/selectedMenuProductFilters';
import * as R from 'ramda';

const isDrawerBox = (type: string) => {
  return type === ProductType.PAX_DRAWER_BOX_ASM;
};

const isDrawerMat = (type: string) => {
  return type === ProductType.PAX_DRAWER_MAT;
};

const isDrawer = (type: string) => {
  return (
    type === ProductType.PAX_DRAWER ||
    type === ProductType.PAX_DRAWER_FRAMED ||
    type === ProductType.PAX_DRAWER_FRAMED_GLASS ||
    type === ProductType.PAX_GLASS_DRAWER
  );
};

const isOrganizer = (type: string) => {
  return (
    type === ProductType.PAX_SKUBB_STORAGE_CASES ||
    type === ProductType.PAX_HEMMAFIXARE_STORAGE_CASE ||
    type === ProductType.PAX_HEMMAFIXARE_STORAGE_CASE_HIGH ||
    type === ProductType.PAX_SKUBB_SHOE_BOXES ||
    type === ProductType.PAX_SKUBB_STORAGE_BOXES ||
    type === ProductType.PAX_HEMMAFIXARE_SHOE_BOX ||
    type === ProductType.PAX_SKUBB_STORAGE_BOXES_W_COMP ||
    type === ProductType.PAX_SKUBB_STORAGE_CASE_WRAP
  );
};

const isHemmafixare = (type: string) => {
  return (
    type === ProductType.PAX_HEMMAFIXARE_STORAGE_CASE ||
    type === ProductType.PAX_HEMMAFIXARE_STORAGE_CASE_HIGH
  );
};

export const isPullOutInterior = (product: Product) => {
  return [
    ProductType.PAX_DRAWER,
    ProductType.PAX_DRAWER_FRAMED,
    ProductType.PAX_DRAWER_FRAMED_GLASS,
    ProductType.PAX_GLASS_DRAWER,
    ProductType.PAX_MESH_BASKET_ASM,
    ProductType.PAX_METAL_BASKET_ASM,
    ProductType.PAX_PULL_OUT_SHOE_SHELF,
    ProductType.PAX_PULL_OUT_TROUSER_HANGER,
    ProductType.PAX_PULL_OUT_TRAY,
    ProductType.PAX_PULL_OUT_TRAY_WITH_SHOE_INSERTS_ASM,
    ProductType.PAX_PULL_OUT_TRAY_WITH_DIVIDER_INSERTS_ASM,
    ProductType.PAX_PULL_OUT_TRAY_WITH_MAT_ASM,
    ProductType.PAX_SHOE_INSERT_FOR_PULL_OUT_TRAY,
    ProductType.PAX_PULL_OUT_TRAY_INSERT,
    ProductType.PAX_PULL_OUT_TRAY_WITH_COMPARTMENT_INSERTS_ASM,
    ProductType.PAX_CLEANING_INTERIOR
  ].some(type => type === product.type);
};

export const isTDivider = (product: Product) => {
  return [ProductType.PAX_T_DIVIDER].some(type => type === product.type);
};

export const matchDrawerDimensions = (
  coreApi: CoreApi,
  selectedProduct: Product | ProductPartial
) => (product: Product | ProductPartial) => {
  if (!isDrawer(selectedProduct.type)) return false;

  if (isDrawerBox(product.type)) {
    return (
      product.communicatedDimensions.depth === selectedProduct.communicatedDimensions.depth &&
      product.communicatedDimensions.width <= selectedProduct.communicatedDimensions.width
    );
  } else if (isDrawerMat(product.type)) {
    return (
      product.communicatedDimensions.width === selectedProduct.communicatedDimensions.width &&
      product.communicatedDimensions.depth === selectedProduct.communicatedDimensions.depth
    );
  }
  return false;
};

const matchHemmafixare = (
  selectedProduct: Product | ProductPartial,
  product: Product | ProductPartial
) => {
  if (
    selectedProduct.communicatedDimensions.depth === 350 &&
    selectedProduct.communicatedDimensions.width > 500
  ) {
    return product.communicatedDimensions.width === 340;
  }

  if (
    product.communicatedDimensions.width <= selectedProduct.communicatedDimensions.width &&
    product.communicatedDimensions.depth < selectedProduct.communicatedDimensions.depth
  ) {
    if (
      product.communicatedDimensions.width === 690 &&
      selectedProduct.communicatedDimensions.width === 1000
    ) {
      return false;
    } else {
      return product.communicatedDimensions.width <= selectedProduct.communicatedDimensions.width;
    }
  }

  return false;
};

export const matchShelfDimensions = (
  coreApi: CoreApi,
  selectedProduct: Product | ProductPartial
) => (product: Product | ProductPartial) => {
  if (
    selectedProduct.type !== ProductType.PAX_SHELF &&
    selectedProduct.type !== ProductType.PAX_T_DIVIDER &&
    selectedProduct.type !== ProductType.PAX_L_DIVIDER
  ) {
    return false;
  }
  if (isOrganizer(product.type)) {
    if (isHemmafixare(product.type)) {
      return matchHemmafixare(selectedProduct, product);
    } else if (
      product.type === ProductType.PAX_SKUBB_SHOE_BOXES ||
      product.type === ProductType.PAX_HEMMAFIXARE_SHOE_BOX
    ) {
      return (
        product.communicatedDimensions.width <= selectedProduct.communicatedDimensions.width &&
        product.communicatedDimensions.depth < selectedProduct.communicatedDimensions.depth
      );
    } else if (product.type === ProductType.PAX_SKUBB_STORAGE_BOXES) {
      if (selectedProduct.communicatedDimensions.depth === 350) {
        return (
          product.communicatedDimensions.width <= selectedProduct.communicatedDimensions.width &&
          product.communicatedDimensions.depth < selectedProduct.communicatedDimensions.depth
        );
      } else {
        return (
          product.communicatedDimensions.width <= selectedProduct.communicatedDimensions.width &&
          product.communicatedDimensions.depth > 350
        );
      }
    } else if (product.type === ProductType.PAX_SKUBB_STORAGE_BOXES_W_COMP) {
      return (
        selectedProduct.communicatedDimensions.width !== 750 &&
        selectedProduct.communicatedDimensions.depth === 350
      );
    }

    return (
      product.communicatedDimensions.width === selectedProduct.communicatedDimensions.width &&
      product.communicatedDimensions.depth < selectedProduct.communicatedDimensions.depth
    );
  }

  return false;
};

export const matchSelectedOrganizersWidth = (
  coreApi: CoreApi,
  selectedProduct: Product | ProductPartial
) => (product: Product | ProductPartial) => {
  const selectedProductWidth = selectedProduct.communicatedDimensions.width;
  const productWidth = product.communicatedDimensions.width;

  // if current width is 1000, allow match if product is Skubb of same width or 500
  if (selectedProductWidth === 1000 && product.type === ProductType.PAX_SKUBB_STORAGE_CASES) {
    return productWidth === selectedProductWidth || productWidth === 500;
  }

  // if current width is 500, allow match if product is Hemmafixare width 440
  if (
    selectedProductWidth === 500 &&
    productWidth === 440 &&
    product.type === ProductType.PAX_HEMMAFIXARE_STORAGE_CASE
  ) {
    return true;
  }

  // if current width is 440, allow match if product is Skubb width 500
  if (
    selectedProductWidth === 440 &&
    productWidth === 500 &&
    product.type === ProductType.PAX_SKUBB_STORAGE_CASES
  ) {
    return true;
  }

  // allow match if same widths
  return product.communicatedDimensions.width === selectedProductWidth;
};

export const matchOrganizerDepth = (
  coreApi: CoreApi,
  selectedProduct: Product | ProductPartial
) => (product: Product | ProductPartial) => {
  if (
    product.type === ProductType.PAX_HEMMAFIXARE_STORAGE_CASE ||
    product.type === ProductType.PAX_SKUBB_STORAGE_CASES
  ) {
    return true;
  } else {
    return product.communicatedDimensions.depth === selectedProduct.communicatedDimensions.depth;
  }
};

export const matchOrganizerType = (coreApi: CoreApi, selectedProduct: Product | ProductPartial) => (
  product: Product | ProductPartial
) => {
  const shelfOrganizerTypes = [
    ProductType.PAX_SKUBB_STORAGE_CASES,
    ProductType.PAX_SKUBB_SHOE_BOXES,
    ProductType.PAX_HEMMAFIXARE_STORAGE_CASE,
    ProductType.PAX_HEMMAFIXARE_STORAGE_CASE_HIGH,
    ProductType.PAX_SKUBB_STORAGE_BOXES,
    ProductType.PAX_HEMMAFIXARE_SHOE_BOX,
    ProductType.PAX_SKUBB_STORAGE_BOXES_W_COMP,
    ProductType.PAX_SKUBB_STORAGE_CASE_WRAP
  ];
  const drawerOrganizerTypes = [ProductType.PAX_DRAWER_BOX_ASM, ProductType.PAX_DRAWER_MAT];
  const hagingOrganizerTypes = [ProductType.PAX_SKUBB_HANGING_STORAGE];
  const shoeOrganizerTypes = [ProductType.PAX_SHOE_MAT];
  return (
    (shelfOrganizerTypes.includes(selectedProduct.type) &&
      shelfOrganizerTypes.includes(product.type)) ||
    (drawerOrganizerTypes.includes(selectedProduct.type) &&
      drawerOrganizerTypes.includes(product.type)) ||
    (hagingOrganizerTypes.includes(selectedProduct.type) &&
      hagingOrganizerTypes.includes(product.type)) ||
    (shoeOrganizerTypes.includes(selectedProduct.type) && shoeOrganizerTypes.includes(product.type))
  );
};

export const interiorFitsCornerFrame = (interior: Product, selectedProduct: Product) => {
  const cornerFrame = selectedProduct.parts.find(
    part => part.product?.type === ProductType.PAX_ADD_ON_CORNER_FRAME_ASM
  )!.product!;
  // All corner frames in all sizes use interiors in size 50x35
  // Exception is the cloth rail for 58cm deep frames also is possible to have in 58cm deep corner frame
  return (
    (cornerFrame.communicatedDimensions.depth === 580 &&
      interior.type === ProductType.PAX_CLOTH_RAIL_580 &&
      interior.communicatedDimensions.width === 500) ||
    (interior.type !== ProductType.PAX_L_DIVIDER &&
      interior.type !== ProductType.PAX_T_DIVIDER &&
      interior.communicatedDimensions.width === 500 &&
      interior.communicatedDimensions.depth === 350)
  );
};

export const matchApplicableFrame = (
  coreApi: CoreApi,
  selectedProduct: Product,
  selectedEntity?: Entity
) => (product: Product): boolean => {
  if (!selectedEntity) {
    return true;
  }

  if (!product.meta || !product.meta.applicableFrameTypes) {
    return true;
  }

  const hasHinged = product.meta.applicableFrameTypes.includes('hinged');
  const hasNonHinged = product.meta.applicableFrameTypes.includes('non-hinged');
  const cornerRegularFrame = product.meta.applicableFrameTypes.includes('cornerRegularFrame');

  if (selectedProduct.type === ProductType.PAX_CORNER_SOLUTION_ASM) {
    return cornerRegularFrame;
  }

  const hingedDoorsOnFrame = coreApi.entities
    .getFrameDescendants(selectedEntity)
    .filter(
      entity =>
        entity.hasComponent(ComponentIds.DOOR_COMPONENT_ID) &&
        !entity.hasComponent(SlidingDoorComponent.id)
    );

  if (hasHinged && hasNonHinged) {
    return true;
  }

  return hingedDoorsOnFrame.length > 0 ? hasHinged : hasNonHinged;
};

export const matchLightWidth = (
  coreApi: CoreApi,
  selectedProduct: Product,
  selectedEntity?: Entity
) => (product: Product) => {
  const diffBetweenNorrflyAndOversidan = 40;
  const diffBetweenFrameAndNorrfly = 80;
  const diffBetweenFrameAndOversidan = 40;
  const diffCornerSolutionNorrfly = 330;
  const diffCornerSolutionOversidan = 290;

  if (
    selectedProduct.type === ProductType.PAX_INNER_LIGHT_OVERSIDAN ||
    selectedProduct.type === ProductType.PAX_INNER_LIGHT
  ) {
    return (
      Math.abs(
        product.communicatedDimensions.width - selectedProduct.communicatedDimensions.width
      ) <= diffBetweenNorrflyAndOversidan
    );
  } else if (selectedProduct.type === ProductType.PAX_T_DIVIDER) {
    const diff =
      selectedProduct.communicatedDimensions.width / 2 - product.communicatedDimensions.width;
    return diff === diffBetweenFrameAndNorrfly || diff === diffBetweenFrameAndOversidan;
  } else if (
    selectedProduct.type === ProductType.PAX_SHELF &&
    selectedProduct.communicatedDimensions.width >= 750 &&
    selectedEntity &&
    HierarchyUtils.findWithComponentInUpwardsHierarchy(selectedEntity, CornerSolutionComponent.id)
  ) {
    return (
      selectedProduct.communicatedDimensions.width - product.communicatedDimensions.width ===
        diffCornerSolutionNorrfly ||
      selectedProduct.communicatedDimensions.width - product.communicatedDimensions.width ===
        diffCornerSolutionOversidan
    );
  } else {
    const diff =
      selectedProduct.communicatedDimensions.width - product.communicatedDimensions.width;
    return diff === diffBetweenFrameAndNorrfly || diff === diffBetweenFrameAndOversidan;
  }
};

export const matchDoorType = (coreApi: CoreApi, selectedProduct: Product) => (product: Product) => {
  const { type } = selectedProduct;
  if (
    ([
      ProductType.PAX_SLIDING_DOOR_REGULAR_ASM,
      ProductType.PAX_SLIDING_DOOR_WITH_PANELS_ASM
    ] as string[]).includes(type)
  ) {
    return true;
  }
  return type === product.type;
};

export const hasPanels = (coreApi: CoreApi, selectedProduct: Product) => () =>
  selectedProduct.type === ProductType.PAX_SLIDING_DOOR_WITH_PANELS_ASM;

export const matchPanelSize = (coreApi: CoreApi, selectedProduct: Product) => (
  product: Product
) => {
  return (
    product.communicatedDimensions.width ===
      (selectedProduct.communicatedDimensions.width === 2000 ? 1000 : 750) &&
    product.communicatedDimensions.height ===
      (selectedProduct.communicatedDimensions.height === 2360 ? 575 : 486)
  );
};

export const matchInteriorWidth = (coreApi: CoreApi, selectedProduct: Product | ProductPartial) => (
  product: Product | ProductPartial
) => {
  if (product.type === ProductType.PAX_L_DIVIDER || product.type === ProductType.PAX_T_DIVIDER) {
    if (selectedProduct.communicatedDimensions.width > 500) {
      return product.communicatedDimensions.width <= selectedProduct.communicatedDimensions.width;
    } else {
      return false;
    }
  }

  return (
    product.type === ProductType.PAX_VALET_HANGER ||
    product.type === ProductType.PAX_PULL_OUT_MULTI_USE_HANGER ||
    matchWidth(coreApi, selectedProduct)(product)
  );
};

export const matchInteriorDepth = (coreApi: CoreApi, selectedProduct: Product | ProductPartial) => (
  product: Product
) => {
  if (product.type === ProductType.PAX_L_DIVIDER || product.type === ProductType.PAX_T_DIVIDER) {
    if (selectedProduct.communicatedDimensions.width > 500) {
      return product.communicatedDimensions.depth === selectedProduct.communicatedDimensions.depth;
    } else {
      return false;
    }
  }

  return (
    product.type === ProductType.PAX_VALET_HANGER ||
    product.type === ProductType.PAX_SHOE_SHELF ||
    matchDepth(coreApi, selectedProduct)(product)
  );
};

export const matchInteriorType = (
  coreApi: CoreApi,
  selectedProduct: Product | ProductPartial,
  selectedEntity?: Entity
) => (product: Product) => {
  const drawerCluster = [
    ProductType.PAX_DRAWER,
    ProductType.PAX_DRAWER_FRAMED,
    ProductType.PAX_DRAWER_FRAMED_GLASS,
    ProductType.PAX_DRAWER_LOCKABLE,
    ProductType.PAX_GLASS_DRAWER,
    ProductType.PAX_MESH_BASKET_ASM,
    ProductType.PAX_METAL_BASKET_ASM
  ];
  const shelfCluster = [
    ProductType.PAX_SHELF,
    ProductType.PAX_GLASS_SHELF,
    ProductType.PAX_PULL_OUT_TRAY,
    ProductType.PAX_PULL_OUT_TRAY_WITH_DIVIDER_INSERTS_ASM,
    ProductType.PAX_PULL_OUT_TRAY_WITH_MAT_ASM,
    ProductType.PAX_PULL_OUT_TRAY_WITH_COMPARTMENT_INSERTS_ASM,
    ProductType.PAX_PULL_OUT_TRAY_WITH_SHOE_INSERTS_ASM,
    ProductType.PAX_SKUBB_STORAGE_CASES,
    ProductType.PAX_SKUBB_SHOE_BOXES,
    ProductType.PAX_HEMMAFIXARE_STORAGE_CASE,
    ProductType.PAX_HEMMAFIXARE_STORAGE_CASE_HIGH,
    ProductType.PAX_SKUBB_STORAGE_BOXES,
    ProductType.PAX_HEMMAFIXARE_SHOE_BOX,
    ProductType.PAX_SKUBB_STORAGE_BOXES_W_COMP,
    ProductType.PAX_SKUBB_STORAGE_CASE_WRAP
  ];
  const cornerShelfCluster = [ProductType.PAX_SHELF, ProductType.PAX_GLASS_SHELF];

  const clothRailCluster = [ProductType.PAX_CLOTH_RAIL_350, ProductType.PAX_CLOTH_RAIL_580];

  const parent = selectedEntity && HierarchyUtils.parent(selectedEntity);

  const isClothRailInCornerFrame =
    clothRailCluster.includes(selectedProduct.type) &&
    parent &&
    coreApi.entities.isProductType(parent, ProductType.PAX_ADD_ON_CORNER_FRAME);

  if (drawerCluster.includes(selectedProduct.type)) {
    return drawerCluster.some(type => type === product.type);
  } else if (shelfCluster.includes(selectedProduct.type)) {
    const cluster =
      selectedEntity &&
      HierarchyUtils.findWithComponentInUpwardsHierarchy(
        selectedEntity,
        CornerSolutionComponent.id
      ) &&
      selectedProduct.communicatedDimensions.width > 500
        ? cornerShelfCluster
        : shelfCluster;

    return cluster.some(type => type === product.type);
  } else if (isClothRailInCornerFrame) {
    return clothRailCluster.some(type => type === product.type);
  }

  return product.type === selectedProduct.type;
};

/**
 * If selected interior is a cloth rail in 580cm deep corner frame,
 * cloth rails in both depths should be displayed, otherwise match exact depth
 */
export const matchSelectedInteriorDepth = (
  coreApi: CoreApi,
  selectedProduct: Product | ProductPartial,
  selectedEntity?: Entity
) => (product: Product) => {
  const clothRailCluster = [ProductType.PAX_CLOTH_RAIL_350, ProductType.PAX_CLOTH_RAIL_580];
  const parent = selectedEntity && HierarchyUtils.parent(selectedEntity);

  const isClothRailInCornerFrame =
    clothRailCluster.includes(selectedProduct.type) &&
    parent &&
    coreApi.entities.isProductType(parent, ProductType.PAX_ADD_ON_CORNER_FRAME);

  return isClothRailInCornerFrame
    ? true
    : product.communicatedDimensions.depth === selectedProduct.communicatedDimensions.depth;
};

const interiorFitsInCornerSolution = (
  coreApi: CoreApi,
  selectedProduct: Product,
  product: Product
) => {
  const regularFrame = selectedProduct.parts.find(
    part => part.product?.type === ProductType.PAX_FRAME
  )!.product!;
  if (isTDivider(product)) {
    return false;
  }
  return isPullOutInterior(product)
    ? interiorFitsCornerFrame(product, selectedProduct)
    : matchInteriorWidth(coreApi, regularFrame)(product) &&
        matchInteriorDepth(coreApi, regularFrame)(product);
};

/**
 * "NormalFrame" referes to the regular frame in a corner
 * solution assembly
 */
const lDividerFitsNormalFrame = (coreApi: CoreApi, selectedProduct: Product, product: Product) => {
  const regularFrame = selectedProduct.parts.find(
    part => part.product?.type === ProductType.PAX_FRAME
  )!.product!;
  return (
    matchInteriorDepth(coreApi, regularFrame)(product) &&
    matchOrientation(coreApi, selectedProduct)(product) &&
    regularFrame.communicatedDimensions.width >= 750
  );
};

export const matchDoorToFrame = (coreApi: CoreApi, selectedProduct: Product) => (
  product: Product
) => {
  const selectedProductDimensions = selectedProduct.communicatedDimensions;
  const productDimensions = product.communicatedDimensions;

  // Only show 250 wide doors for 500 and 750 wide frames
  if (
    (productDimensions.width === 250 && selectedProductDimensions.width === 500) ||
    (productDimensions.width === 250 && selectedProductDimensions.width === 750)
  ) {
    return productDimensions.height === selectedProductDimensions.height;
  }
  // Ensures we get the correct frame for the 375 door
  if (productDimensions.width === 375) {
    return (
      selectedProductDimensions.width === 750 &&
      productDimensions.height === selectedProductDimensions.height
    );
  }
  // Ensures we get no 250 doors if we only have a 1000 wide frame selected
  if (productDimensions.width === 250 && selectedProductDimensions.width === 1000) {
    return false;
  }

  // If no other check falls through then we filter the doors by width and height
  return (
    productDimensions.height === selectedProductDimensions.height &&
    productDimensions.width <= selectedProductDimensions.width
  );
};

export const matchInteriorSuggestionDimensions = (coreApi: CoreApi, selectedProduct: Product) => (
  product: Product
) => {
  if (selectedProduct.type === ProductType.PAX_CORNER_SOLUTION_ASM) {
    const regularFrame = selectedProduct.parts.find(
      part => part.product?.type === ProductType.PAX_FRAME
    );
    return regularFrame?.product
      ? R.equals(product.communicatedDimensions, regularFrame.product.communicatedDimensions)
      : false;
  }

  return R.equals(product.communicatedDimensions, selectedProduct.communicatedDimensions);
};

export const matchInterior = (coreApi: CoreApi, selectedProduct: Product) => (product: Product) => {
  if (product.type === ProductType.PAX_CLEANING_INTERIOR) {
    return selectedProduct.communicatedDimensions.depth === 580;
  } else if (selectedProduct.type === ProductType.PAX_CORNER_SOLUTION_ASM) {
    const cornerInteriorCheck =
      product.type === ProductType.PAX_L_DIVIDER
        ? lDividerFitsNormalFrame(coreApi, selectedProduct, product)
        : interiorFitsInCornerSolution(coreApi, selectedProduct, product);
    return cornerInteriorCheck && matchDesign(coreApi, selectedProduct)(product);
  }
  return (
    matchInteriorWidth(coreApi, selectedProduct)(product) &&
    matchInteriorDepth(coreApi, selectedProduct)(product) &&
    matchOrientation(coreApi, selectedProduct)(product) &&
    matchDesign(coreApi, selectedProduct)(product)
  );
};

export const canHaveHandles = (coreApi: CoreApi, selectedProduct: Product) => () => {
  return selectedProduct.type === ProductType.PAX_HINGE_DOOR_ASM;
};

// Don´t show other products in menu for selected Custom sliding door
export const matchCustomSlidingDoor = (coreApi: CoreApi) => () => {
  const selectedEntity = coreApi.entities.findSelectedEntity();
  return selectedEntity?.hasComponent(CustomSlidingDoorComponent.id) ? false : true;
};

// Dont show doors that cant be on corner addon frame or corner regular frame
export const matchDoorToFrameType = (coreApi: CoreApi) => (product: Product) => {
  if (
    product.meta?.allowOnCornerAddonFrame !== undefined &&
    product.meta?.allowOnCornerAddonFrame === false
  ) {
    const selectedEntity = coreApi.entities.findSelectedEntity();
    const onCornerAddonFrame =
      (selectedEntity &&
        !!HierarchyUtils.findInUpwardsHierarchy(selectedEntity, e =>
          e.hasComponent(CornerAddonFrameComponent.id)
        )) ||
      false;
    return !onCornerAddonFrame;
  }
  if (
    product.meta?.allowOnCornerRegularFrame !== undefined &&
    product.meta?.allowOnCornerRegularFrame === false
  ) {
    const selectedEntity = coreApi.entities.findSelectedEntity();
    const onCornerRegularFrame =
      (selectedEntity &&
        !!HierarchyUtils.findInUpwardsHierarchy(selectedEntity, e =>
          e.hasComponent(CornerRegularFrameComponent.id)
        )) ||
      false;
    return !onCornerRegularFrame;
  }
  return true;
};
